import { useId, useRef, useState, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@dorixona/api'
import { REVIEW_COMMENT_MAX } from '@dorixona/api/reviews'
import { Button, Press, cx, haptic } from '@dorixona/ui'
import { Icon } from '../../../ui/Icon'
import { failureText } from '../../../ui/Load'
import { Sheet } from '../../../ui/Sheet'
import { useSubmitReview } from '../api'
import { STARS, type ReviewTarget } from '../model'

export interface ReviewSheetProps {
  opened: boolean
  /** О каком заказе спрашиваем. Остаётся и пока шторка уезжает. */
  target: ReviewTarget
  /**
   * Шторку закрыли. `rated` — у заказа есть оценка: поставили сейчас или
   * раньше (сервер ответил 409). Иначе покупатель ушёл без оценки.
   */
  onClose: (rated: boolean) => void
}

/**
 * Оценка аптеки после заказа: звёзды 1–5 и необязательный комментарий.
 * Короткая задача в контексте экрана — шторка (HIG sheets.md); слева тихое
 * «Не сейчас», справа «Отправить», смахнуть вниз — тоже «не сейчас».
 * Отправили — в той же шторке «спасибо» и «Готово»: сама она не исчезает
 * (HIG accessibility.md › Minimize use of time-boxed interface elements).
 */
export function ReviewSheet({ opened, target, onClose }: ReviewSheetProps) {
  const { t } = useTranslation('account')
  const [rated, setRated] = useState(false)
  // Каждое открытие — с чистого листа: оценки ещё нет.
  const [wasOpen, setWasOpen] = useState(opened)
  if (opened !== wasOpen) {
    setWasOpen(opened)
    if (opened) setRated(false)
  }

  return (
    <Sheet
      opened={opened}
      label={t('review.title')}
      onClose={() => {
        onClose(rated)
      }}
    >
      <ReviewForm
        target={target}
        onRated={() => {
          setRated(true)
        }}
        onClose={onClose}
      />
    </Sheet>
  )
}

interface ReviewFormProps {
  target: ReviewTarget
  onRated: () => void
  onClose: (rated: boolean) => void
}

function ReviewForm({ target, onRated, onClose }: ReviewFormProps) {
  const { t } = useTranslation(['account', 'common'])
  const submit = useSubmitReview()
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [done, setDone] = useState(false)
  const id = useId()

  if (done) {
    return (
      <div className="fsheet rv-done" role="status">
        <span className="rv-done-mark">
          <Icon name="i-check" size={28} />
        </span>
        <h2>{t('account:review.thanks')}</h2>
        <p>{t('account:review.thanksLead')}</p>
        <Button
          onPress={() => {
            onClose(true)
          }}
        >
          {t('account:review.done')}
        </Button>
      </div>
    )
  }

  const send = () => {
    submit.mutate(
      { orderId: target.orderId, input: { rating, comment } },
      {
        onSuccess: () => {
          haptic('success')
          onRated()
          setDone(true)
        },
        onError: (error) => {
          // Заказ уже оценён (с другого устройства, из другой вкладки) —
          // спрашивать не о чем, закрываемся молча.
          if (isApiError(error) && error.code === 'conflict') {
            onRated()
            onClose(true)
            return
          }
          haptic('error')
        },
      },
    )
  }

  const failed = submit.isError && !(isApiError(submit.error) && submit.error.code === 'conflict')

  return (
    <div className="fsheet rv">
      {target.storeName ? (
        <p className="rv-store">
          <Icon name="i-store" size={16} />
          {target.storeName}
        </p>
      ) : null}
      <h2 className="rv-question">
        {t('account:review.question', { number: target.orderNumber })}
      </h2>

      <Stars value={rating} onChange={setRating} />

      {rating > 0 ? (
        <div className="rv-field">
          <label htmlFor={`${id}-comment`}>{t('account:review.comment')}</label>
          <textarea
            id={`${id}-comment`}
            rows={3}
            maxLength={REVIEW_COMMENT_MAX}
            value={comment}
            placeholder={t('account:review.placeholder')}
            aria-describedby={`${id}-count`}
            onChange={(event) => {
              setComment(event.target.value)
            }}
          />
          <span id={`${id}-count`} className="rv-count num">
            {t('account:review.counter', { count: comment.length, max: REVIEW_COMMENT_MAX })}
          </span>
        </div>
      ) : null}

      {failed ? (
        <p className="rv-error" role="alert">
          {failureText(submit.error, t)}
        </p>
      ) : null}

      <div className="ffoot">
        <Button
          variant="ghost"
          onPress={() => {
            onClose(false)
          }}
        >
          {t('account:review.later')}
        </Button>
        <Button disabled={rating === 0} loading={submit.isPending} onPress={send}>
          {submit.isPending
            ? t('account:review.sending')
            : failed
              ? t('common:actions.retry')
              : t('account:review.submit')}
        </Button>
      </div>
    </div>
  )
}

/** Шаг по стрелкам: вправо и вверх — больше звёзд, влево и вниз — меньше. */
const STEP: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }

interface StarsProps {
  value: number
  onChange: (rating: number) => void
}

/**
 * Пять звёзд — группа переключателей (radiogroup): диктор читает «3 звезды,
 * выбрано», клавиатура ходит стрелками, а в порядке Tab стоит одна звезда —
 * выбранная или первая. Каждая звезда — цель 44pt (HIG accessibility.md ›
 * Offer sufficiently sized controls); выбор отмечен и заливкой, и числом в
 * имени, а не только цветом.
 */
function Stars({ value, onChange }: StarsProps) {
  const { t } = useTranslation('account')
  const refs = useRef<(HTMLElement | null)[]>([])

  const pick = (rating: number) => {
    onChange(rating)
    refs.current[rating - 1]?.focus()
  }

  const onKeyDown = (event: KeyboardEvent) => {
    const step = STEP[event.key]
    let next: number
    if (step) next = Math.min(STARS.length, Math.max(1, value + step))
    else if (event.key === 'Home') next = 1
    else if (event.key === 'End') next = STARS.length
    else return
    event.preventDefault()
    pick(next)
  }

  return (
    <div role="radiogroup" aria-label={t('review.rating')} className="rv-stars">
      {STARS.map((n) => (
        <Press
          key={n}
          squish="icon"
          haptic="select"
          role="radio"
          aria-checked={value === n}
          aria-label={t('review.stars', { count: n })}
          tabIndex={(value || 1) === n ? 0 : -1}
          className={cx('rv-star', n <= value && 'on')}
          onKeyDown={onKeyDown}
          elementRef={(node: HTMLElement | null) => {
            refs.current[n - 1] = node
          }}
          onPress={() => {
            pick(n)
          }}
        >
          <Icon name="i-star" size={36} />
        </Press>
      ))}
    </div>
  )
}
